import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, Flame, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const FatBurnerPage = () => {
  return (
    <>
      <Helmet>
        <title>Buy Fat Burners in India & Nepal – Safe & Effective | NutraFuel®</title>
        <meta name="description" content="Shop safe, effective fat burner supplements in India & Nepal. Thermogenic formulas for weight loss & fat burning. 100% authentic products. Expert guidance available." />
        <meta name="keywords" content="buy fat burner india, fat loss supplements nepal, thermogenic india, weight loss supplements india, safe fat burners" />
        <link rel="canonical" href="https://nutrafuel.in/fat-burner" />
        <meta property="og:title" content="Buy Fat Burners in India & Nepal – Safe & Effective" />
        <meta property="og:description" content="Safe, effective fat burner supplements for weight loss goals." />
        <meta property="og:url" content="https://nutrafuel.in/fat-burner" />
      </Helmet>

      <Layout>
        {/* Breadcrumb */}
        <div className="bg-muted/30 py-3 border-b border-border">
          <div className="container mx-auto px-4">
            <nav className="text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground font-medium">Fat Burners</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-orange-500 text-white">Fat Loss & Definition</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6">
                Buy Fat Burners in India & Nepal
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Shop premium fat burner supplements in India and Nepal. Our thermogenic formulas help accelerate fat loss, boost metabolism, and maintain lean muscle.
              </p>
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <article className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  What Are Fat Burners?
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Fat burner supplements are thermogenic formulas designed to support fat loss and weight management through multiple mechanisms.
                </p>

                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Benefits of Fat Burner Supplements
                </h2>

                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <Flame className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div><strong>Increased Metabolism:</strong> Boost your metabolic rate naturally</div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Flame className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                    <div><strong>Enhanced Energy:</strong> Stay energized during calorie deficits</div>
                  </li>
                </ul>

                <div className="bg-orange-50 border-l-4 border-orange-500 rounded p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-foreground mb-2">Safety Note:</h4>
                      <p className="text-sm text-muted-foreground">
                        Consult a healthcare professional before using fat burners. Not suitable for individuals under 18 or with heart conditions.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                  Common Questions
                </h2>

                <div className="space-y-6">
                  <div className="border-b border-border pb-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      Do fat burners really work?
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      Fat burners can enhance fat loss by 10-15% when combined with proper diet and exercise. They work best as tools alongside calorie deficit and training.
                    </p>
                  </div>
                </div>

                {/* Related Categories */}
                <div className="mt-12 pt-8 border-t border-border">
                  <h2 className="text-2xl font-display font-bold text-foreground mb-6">
                    Related Products
                  </h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link to="/whey-protein" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Whey Protein</h4>
                      <p className="text-sm text-muted-foreground">Preserve muscle</p>
                    </Link>
                    <Link to="/pre-workout" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">Pre-Workout</h4>
                      <p className="text-sm text-muted-foreground">Energy boost</p>
                    </Link>
                    <Link to="/products" className="block p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
                      <h4 className="font-bold text-foreground mb-2">All Products</h4>
                      <p className="text-sm text-muted-foreground">Browse all</p>
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-primary/10 via-gold/10 to-primary/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-display font-bold text-foreground mb-4">
              Start Your Fat Loss Journey
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Get personalized recommendations from our experts.
            </p>
            <Button variant="default" size="lg" className="bg-green-600 hover:bg-green-700" asChild>
              <a href="https://wa.me/9779824042067?text=Hi!%20I%20want%20fat%20loss%20advice" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat with Expert
              </a>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default FatBurnerPage;
